import { cva } from 'class-variance-authority';

/**
 * Content panel class recipe.
 *
 * Like `Button.recipe`, `TextArea.recipe`, `Accordion.recipe` and `Stepper.recipe`, this
 * contains ZERO `dark:` variants — colour comes entirely from the `--ds-panel-*` slots that
 * `.ds-panel` and `.ds-panel-<tier>` set in `components/styles/design-system.css`, and none of
 * those three classes needs a dark rule at all because every slot they set names a semantic role
 * and a role already carries a value per theme.
 *
 * ── The rim is decorative, and its faintness is the design ─────────────────────────────────────
 *
 * The one thing to read before changing anything here. Each tier's rim is bound to the decorative
 * border role paired with that tier's own fill, which measures 1.21:1 and 1.20:1 light, 1.09:1 and
 * 1.16:1 dark. Those numbers are correct and are not a contrast finding: the role collection
 * reserves `--border-control`, `--border-focus` and the status borders for a boundary that carries
 * MEANING, and a panel edge carries none — it groups content that is legible without it.
 *
 * `.ds-accordion` deliberately went the other way, because its card encloses a button and so
 * IDENTIFIES a control. Do not carry that reasoning across. The long note beside `.ds-panel` in
 * the stylesheet has the full argument, and `styles/__tests__/contentPanelContrast.test.ts`
 * asserts the pairing, bans the meaning-carrying roles, and pins the measurement inside the
 * decorative band rather than above 3:1.
 *
 * ── There is no state layer, and no layout either ──────────────────────────────────────────────
 *
 * No hover, pressed, focus or disabled: a panel is not a control, nothing in it is focusable
 * unless a caller put something focusable inside, and a fill that shifted under the pointer would
 * read as an affordance that is not there. That is the same reason the step indicator's recipe is
 * short, arrived at from the other direction — it is inert by type, this is inert by being a box.
 *
 * The absence of LAYOUT is the less obvious half. There is no `flex`, no `gap` and no `w-full`.
 * The requirement is that arbitrary content can be grouped, and a flex column would only stack the
 * panel's DIRECT children — so a caller who wrapped two of them in a fragment or a div would get a
 * result different from the one they drew, for a reason nothing on screen explains. A caller who
 * wants a stack composes one inside, which is one line and is visible where they wrote it. Block
 * flow also means the padding is what stops the empty panel collapsing to a 2px line.
 *
 * ── Where the geometry comes from, and what has changed since ─────────────────────────────────
 *
 * Not from this component's own drawing, because it has none. The `Content Block` set could not be
 * opened when this was written; it can be now, and it was ported on 2026-09-22 into the separate
 * `components/ContentBlock` component from node `1505:28837`.
 *
 * That answers the question this block used to end on — "which `Content Block` this is" — and the
 * answer is that it is NOT this one. The two components are deliberately different things and
 * nothing here was retrofitted to the set:
 *
 *   `ContentPanel`  a bare `<div>`, no role, no heading, no imposed layout. A box a caller fills.
 *   `ContentBlock`  the drawn set: a REQUIRED heading, an optional description, body and footer, a
 *                   `<section>` always named by its own heading, and its own surface and layout
 *                   axes at 24/16/8px insets.
 *
 * So this panel keeps its borrowed geometry on purpose. Changing it to the set's numbers would move
 * every existing panel to match a drawing of a component that is not this one — and a caller who
 * wants the drawing now has it. If you are giving a `ContentPanel` a manual `useId`, an `<h2>`, a
 * `role="region"` and an `aria-labelledby`, you want `ContentBlock`.
 *
 * The three values below remain borrowed from components that DO have a drawing:
 *
 *   radius    `rounded-md`. Confirmed off the Text Area frame, where the library's `md` key is
 *             16px, rather than guessed. Named as the token rather than written as
 *             `rounded-[16px]`, because the library defines this key and the layer's own guidance
 *             is to prefer the four it defines — so a library radius change reaches this
 *             component without an edit here. (`ContentBlock`'s own set confirms the same 16px for
 *             its inline cells, and uses `rounded-lg` at 24px for its stacked ones.)
 *   padding   16px on all four sides, the alert's inset. See below: two measured precedents
 *             disagree, and this is the one that transfers.
 *   rim       1px, from the slot, matching the field and accordion edges.
 *
 * ── Why 16px and not the field's 24px ──────────────────────────────────────────────────────────
 *
 * Both numbers are measured, so this is not a guess against a measurement — it is a choice between
 * two. The alert draws a 16px inset on all four sides; the Text Area draws 24px horizontal and
 * 11px vertical.
 *
 * The alert's is the one that transfers, for two reasons. It is a CONTAINER inset: the alert is a
 * bordered box holding a title and a paragraph, which is the same shape this is, and the two will
 * sit stacked in the same form flow where a shared inset is what makes them read as one material.
 * The Text Area's is a FIELD inset — asymmetric, and vertically driven by the floating-label
 * arithmetic that puts the label and the value in one 60px box. There is no uniform number to take
 * from it: adopting its 24px horizontally would leave the vertical inset invented, which is worse
 * than borrowing a whole measured pair.
 *
 * The structure is therefore as open to the design review as the colour is — but the `Content
 * Block` question is settled, as the provenance note above records: the reworked set lives on the
 * `Content Block [In progress]` page as node `1505:28837` and is ported separately.
 */
export const contentPanelRecipe = cva(
  [
    'ds-panel',
    // `font-sans` for the reason the alert and the tag set it: this layer omits Tailwind's
    // preflight, so without it a panel's copy is typeset by whatever happens to be above it in the
    // tree, and the same panel reads differently on a DS 2.0 page and inside a legacy MUI screen.
    'font-sans',
    'rounded-md border-solid border-[length:var(--ds-panel-border-width)] p-[16px]',
    'bg-[var(--ds-panel-bg)] border-[var(--ds-panel-border)] text-[var(--ds-panel-fg)]',
  ],
  {
    variants: {
      /**
       * Selects a slot class rather than carrying colour itself, so this file has no hex values
       * and no theme branches at all — the same shape as the alert's `severity`.
       *
       * It DOES have a default, where the alert's severity deliberately does not. Severity is a
       * statement about the message and guessing it would put the wrong word in a user's mouth;
       * a surface tier is a statement about where the panel sits, and `primary` — a panel lifted
       * off the page — is what a caller who has not thought about it wants. The tier is also
       * cheap to be wrong about: it changes how far the box steps off its background, not what it
       * claims.
       */
      surface: {
        primary: 'ds-panel-primary',
        secondary: 'ds-panel-secondary',
      },
    },
    defaultVariants: { surface: 'primary' },
  }
);
